import React from 'react';
import { useRecoilValue } from 'recoil';
import {
  BlockOuterContainer,
  CorrelationBlock,
  DistributionBlock,
  FactorContributionBlock,
  FactorTrendBlock,
  FinancialPerformanceIndicatorsBlock,
  HoldingsBlock,
  NotableHistoricalBlock,
  OptionsPerformanceSummaryBlock,
  PeerGroupAnalysisBlock,
  PerformanceAndRiskBlock,
  PortfolioComparisonBlock,
  PortfolioCompositionBlock,
  PortfolioContributionBlock,
  PrivateAssetGrowthSimulationBlock,
  PrivateAssetSummaryBlock,
  PrivateCashFlowBlock,
  PrivatePerformanceSummaryBlock,
  PrivateTimeSeriesBlock,
  ReturnsGridBlock,
  RollingCorrelationBlock,
  SensitivityAnalysisBlock,
  TaxImpactBlock,
  TextFormatBlock,
  TimeSeriesBlock,
  TopHoldingsBlock,
} from 'venn-components';
import { blockDisplayHeader, type BlockId, blockSettings } from 'venn-state';
import { assertExhaustive, type CustomBlockTypeEnum } from 'venn-utils';
import { GrowthSimulationBlock } from './custom-blocks/growth-simulation-block';
import { HistoricalAllocationsTimeSeriesBlock } from './custom-blocks/historical-allocations-timeseries-block';
import { type BlockWidths, useComputeBlockWidth } from './utils/hooks/use-compute-block-width';

type BlockContainerProps = {
  id: string;
  index: number;
} & BlockWidths;

export const BlockContainer = React.memo((props: BlockContainerProps) => {
  const blockSetting = useRecoilValue(blockSettings(props.id));
  const customBlockType = blockSetting.customBlockType;
  const { id } = props;
  const title = useRecoilValue(blockDisplayHeader(id));
  const { blockWidthPx } = useComputeBlockWidth(props);

  const BlockComponent = getBlockComponent(customBlockType);

  return (
    <BlockOuterContainer id={id} title={title} width={blockWidthPx}>
      <BlockComponent id={id} />
    </BlockOuterContainer>
  );
});

BlockContainer.displayName = 'BlockContainer';

const hasSupportedBlock = [
  'NOTABLE_PERIODS',
  'PERFORMANCE_SUMMARY',
  'PRIVATE_CASH_FLOW',
  'PRIVATE_CAPITAL_SUMMARY',
  'PORTFOLIO_BREAKDOWN',
  'PORTFOLIO_COMPARISON',
  'PORTFOLIO_CONTRIBUTION',
  'PRIVATE_PERFORMANCE_TIME_SERIES',
  'PRIVATE_PERFORMANCE_SUMMARY',
  'PUBLIC_PRIVATE_ASSET_GROWTH_PERCENTILES',
  'PUBLIC_PRIVATE_ASSET_GROWTH_BREAKDOWN',
  'ASSET_EXPOSURE',
  'SECTOR_EXPOSURE',
  'GEOGRAPHY_EXPOSURE',
  'HISTORICAL_ALLOCATIONS_TIMESERIES',
  'DISTRIBUTION',
  'GROWTH_SIMULATION',
  'SCENARIO',
  'RETURNS_GRID',
  'TIMESERIES',
  'FACTOR_CONTRIBUTION',
  'CORRELATION',
  'FACTOR_CONTRIBUTION_TREND',
  'PEER_GROUPS',
  'ROLLING_CORRELATION',
  'TOP_HOLDINGS',
  'MARKDOWN',
  'IMAGE',
  'PAGE_BREAK',
  'CREDIT_BREAKDOWN',
  'MATURITY_BREAKDOWN',
  'DURATION_BREAKDOWN',
  'MUNI_BREAKDOWN',
  'FINANCIAL_PERFORMANCE_INDICATORS',
  'OPTIONS_PERFORMANCE_SUMMARY',
  'TAX_IMPACT',
] as const satisfies CustomBlockTypeEnum[];
type SupportedBlockType = (typeof hasSupportedBlock)[number];

export function getBlockComponent(supportedBlockType: SupportedBlockType): React.FC<{ id: BlockId }> {
  switch (supportedBlockType) {
    case 'NOTABLE_PERIODS':
      return NotableHistoricalBlock;
    case 'PERFORMANCE_SUMMARY':
      return PerformanceAndRiskBlock;
    case 'PRIVATE_CASH_FLOW':
      return PrivateCashFlowBlock;
    case 'PORTFOLIO_BREAKDOWN':
      return PortfolioCompositionBlock;
    case 'PORTFOLIO_COMPARISON':
      return PortfolioComparisonBlock;
    case 'PORTFOLIO_CONTRIBUTION':
      return PortfolioContributionBlock;
    case 'PRIVATE_CAPITAL_SUMMARY':
      return PrivateAssetSummaryBlock;
    case 'PRIVATE_PERFORMANCE_TIME_SERIES':
      return PrivateTimeSeriesBlock;
    case 'PRIVATE_PERFORMANCE_SUMMARY':
      return PrivatePerformanceSummaryBlock;
    case 'PUBLIC_PRIVATE_ASSET_GROWTH_PERCENTILES':
    case 'PUBLIC_PRIVATE_ASSET_GROWTH_BREAKDOWN':
      return PrivateAssetGrowthSimulationBlock;
    case 'ASSET_EXPOSURE':
    case 'SECTOR_EXPOSURE':
    case 'GEOGRAPHY_EXPOSURE':
    case 'CREDIT_BREAKDOWN':
    case 'MATURITY_BREAKDOWN':
    case 'DURATION_BREAKDOWN':
    case 'MUNI_BREAKDOWN':
      return HoldingsBlock;
    case 'HISTORICAL_ALLOCATIONS_TIMESERIES':
      return HistoricalAllocationsTimeSeriesBlock;
    case 'DISTRIBUTION':
      return DistributionBlock;
    case 'GROWTH_SIMULATION':
      return GrowthSimulationBlock;
    case 'SCENARIO':
      return SensitivityAnalysisBlock;
    case 'RETURNS_GRID':
      return ReturnsGridBlock;
    case 'TIMESERIES':
      return TimeSeriesBlock;
    case 'FACTOR_CONTRIBUTION':
      return FactorContributionBlock;
    case 'CORRELATION':
      return CorrelationBlock;
    case 'FACTOR_CONTRIBUTION_TREND':
      return FactorTrendBlock;
    case 'PEER_GROUPS':
      return PeerGroupAnalysisBlock;
    case 'ROLLING_CORRELATION':
      return RollingCorrelationBlock;
    case 'TOP_HOLDINGS':
      return TopHoldingsBlock;
    case 'FINANCIAL_PERFORMANCE_INDICATORS':
      return FinancialPerformanceIndicatorsBlock;
    case 'OPTIONS_PERFORMANCE_SUMMARY':
      return OptionsPerformanceSummaryBlock;
    case 'TAX_IMPACT':
      return TaxImpactBlock;
    case 'MARKDOWN':
    case 'IMAGE':
    case 'PAGE_BREAK':
      return TextFormatBlock;
    default:
      throw assertExhaustive(supportedBlockType);
  }
}
